// Profile Configuration Drawer (WP-11 / WhatChimp §1.8).
// Add / Edit profile drawer with exact provider configuration fields,
// password reveal toggles, secret masking (••••••••), and inline connection testing.

import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import { useToast } from '../ui/Toast';
import {
  type IntegrationKind,
  type ProviderName,
  type IntegrationProfileDoc,
  PROVIDER_REGISTRY,
  EMAIL_PROVIDERS,
  SMS_PROVIDERS,
  AUTORESPONDER_PROVIDERS,
  prepareProfileConfigForSave,
  integrationProfileSave,
  integrationProfileTest,
} from '../../lib/integrations';
import {
  CheckIcon,
  AlertCircleIcon,
  RefreshIcon,
} from '../icons';

interface ProfileConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  kind: IntegrationKind;
  profile?: IntegrationProfileDoc | null;
  onSaved: (savedDoc: IntegrationProfileDoc) => void;
}

function EyeIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}

export function ProfileConfigDrawer({
  open,
  onClose,
  kind,
  profile,
  onSaved,
}: ProfileConfigDrawerProps) {
  const { t } = useI18n();
  const toast = useToast();

  const availableProviders: ProviderName[] =
    kind === 'email'
      ? [...EMAIL_PROVIDERS]
      : kind === 'sms'
        ? [...SMS_PROVIDERS]
        : [...AUTORESPONDER_PROVIDERS];

  const [selectedProvider, setSelectedProvider] = useState<ProviderName>(
    profile?.provider || availableProviders[0]
  );
  const [name, setName] = useState(profile?.name || '');
  const [config, setConfig] = useState<Record<string, any>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Live Test states
  const [testRecipient, setTestRecipient] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean;
    verified: boolean;
    lastError?: string | null;
  } | null>(null);

  // Reset form when drawer opens or profile changes
  useEffect(() => {
    if (open) {
      if (profile) {
        setSelectedProvider(profile.provider);
        setName(profile.name);
        // Initialize config with profile config (which may contain '••••••••' for secrets)
        setConfig({ ...profile.config });
        setTestResult(
          profile.lastTestAt
            ? {
                ok: profile.verified,
                verified: profile.verified,
                lastError: profile.lastError,
              }
            : null
        );
      } else {
        const defaultProv = availableProviders[0];
        setSelectedProvider(defaultProv);
        setName('');
        setConfig({});
        setTestResult(null);
      }
      setShowSecrets({});
      setFormError(null);
      setTestRecipient('');
    }
  }, [open, profile, kind]);

  const providerMeta = PROVIDER_REGISTRY[selectedProvider];

  const handleFieldChange = (key: string, value: any) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSecretVisibility = (key: string) => {
    setShowSecrets((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleProviderSelect = (prov: ProviderName) => {
    setSelectedProvider(prov);
    setConfig({});
    setShowSecrets({});
    setTestResult(null);
  };

  const handleTestConnection = async () => {
    if (!profile?.id) {
      toast.warning(t('integrations.saveBeforeTest'));
      return;
    }

    setTesting(true);
    setTestResult(null);
    try {
      const testCallRes = await integrationProfileTest({
        id: profile.id,
        testRecipient: testRecipient.trim() || undefined,
      });
      setTestResult(testCallRes.data);
      if (testCallRes.data.verified) {
        toast.success(t('integrations.testSuccess'));
      } else {
        toast.error(testCallRes.data.lastError || t('integrations.testFailed'));
      }
    } catch (err: any) {
      setTestResult({
        ok: false,
        verified: false,
        lastError: err.message || 'Connection test failed',
      });
      toast.error(err.message || t('integrations.testFailed'));
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    setFormError(null);
    const trimmedName = name.trim();
    if (!trimmedName) {
      setFormError(t('integrations.errorNameRequired'));
      return;
    }

    // Validate required fields
    for (const field of providerMeta.fields) {
      if (field.required) {
        const val = config[field.key];
        const isSecret = providerMeta.secretFields.includes(field.key);
        // If it's a secret and profile exists, '••••••••' or empty when original had value is valid
        if (isSecret && profile) {
          if (!val && !profile.config?.[field.key]) {
            setFormError(t('integrations.errorFieldRequired', { field: field.defaultLabel }));
            return;
          }
        } else if (val === undefined || val === null || val === '') {
          setFormError(t('integrations.errorFieldRequired', { field: field.defaultLabel }));
          return;
        }
      }
    }

    setSaving(true);
    try {
      const preparedConfig = prepareProfileConfigForSave(
        selectedProvider,
        config,
        profile?.config
      );

      const saveRes = await integrationProfileSave({
        id: profile?.id,
        kind,
        provider: selectedProvider,
        name: trimmedName,
        config: preparedConfig,
      });

      if (!saveRes.data.ok) {
        throw new Error('Save failed');
      }

      toast.success(
        profile
          ? t('integrations.profileUpdated')
          : t('integrations.profileCreated')
      );

      const savedDoc: IntegrationProfileDoc = {
        id: saveRes.data.id || profile?.id || 'new',
        tenantId: profile?.tenantId || '',
        kind,
        provider: selectedProvider,
        name: trimmedName,
        config: preparedConfig,
        secretFields: providerMeta.secretFields,
        verified: profile ? profile.verified : false,
        lastTestAt: profile ? profile.lastTestAt : null,
        lastError: profile ? profile.lastError : null,
        createdAt: profile?.createdAt || Date.now(),
        updatedAt: Date.now(),
      };

      onSaved(savedDoc);
      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save integration profile.');
      toast.error(err.message || 'Failed to save integration profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        profile
          ? t('integrations.editProfile')
          : t('integrations.addProfile')
      }
      subtitle={
        kind === 'email'
          ? t('integrations.emailSubtitle')
          : kind === 'sms'
            ? t('integrations.smsSubtitle')
            : t('integrations.autoresponderSubtitle')
      }
      size="lg"
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {formError && (
          <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 p-3.5 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
            <AlertCircleIcon size={18} className="shrink-0" />
            <span>{formError}</span>
          </div>
        )}

        {/* Provider Selection (only when adding new profile) */}
        {!profile ? (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-slate-400 mb-2">
              {t('integrations.selectProvider')}
            </label>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {availableProviders.map((prov) => {
                const meta = PROVIDER_REGISTRY[prov];
                const isSelected = selectedProvider === prov;
                return (
                  <button
                    key={prov}
                    type="button"
                    onClick={() => handleProviderSelect(prov)}
                    className={`flex flex-col items-start p-3 rounded-xl border text-start transition ${
                      isSelected
                        ? 'border-brand bg-brand/5 dark:bg-brand/10 dark:border-brand ring-2 ring-brand/20'
                        : 'border-line bg-white hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700'
                    }`}
                  >
                    <span className="text-xl mb-1">{meta.icon}</span>
                    <span className="text-sm font-medium text-ink dark:text-slate-100 line-clamp-1">
                      {meta.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-3.5 rounded-xl border border-line bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
            <span className="text-2xl">{providerMeta.icon}</span>
            <div>
              <div className="text-sm font-bold text-ink dark:text-slate-100">{providerMeta.name}</div>
              <div className="text-xs text-ink-3 dark:text-slate-400">{providerMeta.defaultDesc}</div>
            </div>
          </div>
        )}

        {/* Profile Name */}
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-slate-400 mb-1.5">
            {t('integrations.profileName')} <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('integrations.profileNamePlaceholder')}
            className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
          />
        </div>

        {/* Dynamic Provider Configuration Fields */}
        <div className="space-y-4 pt-2 border-t border-line dark:border-slate-800">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-slate-400">
            {t('integrations.connectionConfig')}
          </div>

          {providerMeta.fields.map((field) => {
            const val = config[field.key] ?? '';
            const isSecret = providerMeta.secretFields.includes(field.key);
            const isRevealed = showSecrets[field.key] || false;

            return (
              <div key={field.key}>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-ink-2 dark:text-slate-300">
                    {field.defaultLabel} {field.required && <span className="text-rose-500">*</span>}
                  </label>
                  {isSecret && (
                    <button
                      type="button"
                      onClick={() => toggleSecretVisibility(field.key)}
                      className="inline-flex items-center gap-1 text-xs text-brand hover:underline"
                    >
                      {isRevealed ? (
                        <>
                          <EyeOffIcon size={14} />
                          <span>{t('common.hide')}</span>
                        </>
                      ) : (
                        <>
                          <EyeIcon size={14} />
                          <span>{t('common.show')}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {field.type === 'select' ? (
                  <select
                    value={val}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {(field.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'boolean' ? (
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={Boolean(val)}
                      onChange={(e) => handleFieldChange(field.key, e.target.checked)}
                      className="h-4 w-4 rounded border-line text-brand focus:ring-brand"
                    />
                    <span className="text-xs text-ink-2 dark:text-slate-300">{field.defaultLabel}</span>
                  </label>
                ) : field.type === 'textarea' ? (
                  <textarea
                    rows={3}
                    value={val}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="w-full font-mono text-xs rounded-xl border border-line bg-white px-3.5 py-2.5 text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                ) : (
                  <input
                    type={isSecret && !isRevealed ? 'password' : field.type === 'number' ? 'number' : 'text'}
                    value={val}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    placeholder={
                      isSecret && profile && !val ? '••••••••' : field.placeholder
                    }
                    className="w-full rounded-xl border border-line bg-white px-3.5 py-2.5 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
                  />
                )}

                {isSecret && profile && (
                  <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-500">
                    {t('integrations.secretLeaveBlankHelp')}
                  </p>
                )}

                {field.defaultHelperText && (
                  <p className="mt-1 text-[11px] text-ink-3 dark:text-slate-500">
                    {field.defaultHelperText}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Inline Live Test Section */}
        <div className="pt-4 border-t border-line dark:border-slate-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-ink-3 dark:text-slate-400">
              {t('integrations.liveTestTitle')}
            </span>
            {testResult && (
              <span
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                  testResult.verified
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                    : 'bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
                }`}
              >
                {testResult.verified ? (
                  <>
                    <CheckIcon size={12} />
                    <span>{t('integrations.verified')}</span>
                  </>
                ) : (
                  <>
                    <AlertCircleIcon size={12} />
                    <span>{t('integrations.failed')}</span>
                  </>
                )}
              </span>
            )}
          </div>

          <p className="text-xs text-ink-3 dark:text-slate-400 mb-3">
            {kind === 'email'
              ? t('integrations.testEmailHelp')
              : kind === 'sms'
                ? t('integrations.testSmsHelp')
                : t('integrations.testAutoresponderHelp')}
          </p>

          <div className="flex items-center gap-2">
            <input
              type="text"
              value={testRecipient}
              onChange={(e) => setTestRecipient(e.target.value)}
              placeholder={
                kind === 'email'
                  ? 'test@example.com'
                  : kind === 'sms'
                    ? '+1234567890 (E.164)'
                    : t('integrations.optionalRecipient')
              }
              className="flex-1 rounded-xl border border-line bg-white px-3.5 py-2 text-sm text-ink outline-none transition focus:border-brand focus:ring-2 focus:ring-brand/20 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100"
            />
            <Button
              variant="secondary"
              size="sm"
              onClick={handleTestConnection}
              disabled={testing || !profile?.id}
            >
              {testing ? (
                <>
                  <RefreshIcon size={14} className="animate-spin" />
                  <span>{t('common.testing')}</span>
                </>
              ) : (
                t('integrations.testConnection')
              )}
            </Button>
          </div>

          {!profile?.id && (
            <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-400">
              {t('integrations.saveFirstToTest')}
            </p>
          )}

          {testResult?.lastError && (
            <div className="mt-3 p-3 rounded-xl border border-rose-200 bg-rose-50 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
              <span className="font-semibold">{t('common.error')}:</span> {testResult.lastError}
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
}
export default ProfileConfigDrawer;
